import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Logo } from './Logo';

const Verify2FA: React.FC = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Get email from sessionStorage if available
    const storedEmail = sessionStorage.getItem('pendingLoginEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/verify-2fa-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, remember_me: rememberDevice }),
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Success - clear sessionStorage and redirect to newsfeed
        sessionStorage.removeItem('pendingLoginEmail');
        window.location.href = '/newsfeed';
      } else {
        // Error response
        setError(data.error || 'Invalid verification code. Please try again.');
        setCode(''); // Clear code on error
        
        // If session expired, redirect to login
        if (response.status === 401 && data.error?.includes('Session expired')) {
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      }
    } catch (err: any) {
      console.error('2FA verification error:', err);
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await fetch('/resend-2fa-code', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setError(null);
          // Show success message (you could add a success state)
          alert('A new verification code has been sent to your email.');
        } else {
          setError(data.message || 'Failed to resend code. Please try logging in again.');
        }
      } else {
        setError('Failed to resend code. Please try logging in again.');
      }
    } catch (err) {
      console.error('Resend code error:', err);
      setError('Failed to resend code. Please try logging in again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo showText={true} />
          </div>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
            <Shield className="h-6 w-6 text-indigo-600" />
          </div>
          <h2 className="text-3xl font-extrabold text-slate-900">
            Two-Factor Authentication
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            {email ? `Enter the verification code sent to ${email}` : 'Enter the verification code sent to your email'}
          </p>
        </div>

        {/* Verification Form */}
        <div className="bg-white rounded-xl shadow-card border border-gray-100 p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-rose-800">Error</p>
                  <p className="text-sm text-rose-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(e) => {
                  // Only allow numbers
                  const value = e.target.value.replace(/\D/g, '');
                  setCode(value);
                }}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500 text-center">
                Enter the 6-digit code from your email
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Remember this device for 30 days
            </label>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Didn't receive a code? Resend
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-gray-600 hover:text-gray-500 flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Verify2FA;
